<template>
  <div class="main" data-app="true">
    <JumbotronView :message="'Opt Out'" />
    <v-app class="app-style mx-auto">
      <div class="card">
        <div id="intro-line-wrapper" class="form-title">
          <p id="intro-line" class="element-sizing">
            If you would like to limit how NetCredit, Republic Bank & Trust Company,
            Transportation Alliance Bank, Inc. d/b/a TAB Bank, and Capital Community Bank share your personal information,
            please complete the opt out request below.
          </p>
        </div>

        <v-alert
          v-if="displayMessage"
          min-width="300"
          class="v-alert__content"
          :class="opt_out_success"
        >
          <div v-html="this.opt_out_message"></div>
        </v-alert>

        <v-subheader style="margin-left: 15%">
          For security purposes we need to verify your identity.
        </v-subheader>

        <v-form
          class="opt_out_form"
        >
          <v-row justify="center">
            <v-col class="left-column" cols="4">
              <v-row>
                <v-subheader>First Name </v-subheader>
              </v-row>
              <v-row>
                <v-text-field
                  id="first-name-input"
                  v-model="firstName"
                  :disabled="loggedIn"
                  :name="'first-name-input'"
                  class="first-name-input input"
                  :error-messages='firstNameErrors'
                  outlined
                  required
                  @input="$v.firstName.$touch()"
                  @blur="$v.firstName.$touch()"
                >
                </v-text-field>
              </v-row>
            </v-col>
            <v-col class="right-column" cols="4">
              <v-row>
                <v-subheader>Last Name </v-subheader>
              </v-row>
              <v-row>
                <v-text-field
                  id="last-name-input"
                  v-model="lastName"
                  :disabled="loggedIn"
                  :name="'last-name-input'"
                  class="last-name-input input"
                  :error-messages='lastNameErrors'
                  outlined
                  required
                  @input="$v.lastName.$touch()"
                  @blur="$v.lastName.$touch()"
                >
                </v-text-field>
              </v-row>
            </v-col>
          </v-row>
          <v-row justify="center">
            <v-col class="left-column" cols="4">
              <v-row>
                <v-subheader>Social Security Number </v-subheader>
              </v-row>
              <v-row>
                <v-text-field
                  id="social-security-input"
                  v-model="ssn"
                  :disabled="loggedIn"
                  :name="'social-security-input'"
                  class="social-security-input input"
                  :error-messages='ssnErrors'
                  :placeholder="'111111111'"
                  oninput="this.value = this.value.replace(/\D/gm, '')"
                  append-icon="mdi-lock"
                  outlined
                  required
                  maxlength="9"
                  @input="$v.ssn.$touch()"
                  @blur="$v.ssn.$touch()"
                >
                </v-text-field>
              </v-row>
            </v-col>
            <v-col class="right-column" cols="4">
              <v-row>
                <v-subheader>Date of Birth </v-subheader>
              </v-row>
              <v-row>
                <v-text-field
                  id="date-of-birth-input"
                  v-model='dob'
                  :disabled="loggedIn"
                  :name="'date-of-birth-input'"
                  class="date-of-birth-input input"
                  :error-messages='dobErrors'
                  :placeholder="'mm/dd/yyyy'"
                  oninput="this.value = this.value.replace(/[^0-9/]/g, '').replace(/\/\//g, '/')"
                  append-icon="mdi-calendar-blank"
                  outlined
                  required
                  maxlength="10"
                  @input="$v.dob.$touch()"
                  @blur="$v.dob.$touch()"
                >
                </v-text-field>
              </v-row>
            </v-col>
          </v-row>

          <v-row justify="center">
            <v-col cols="8">
              <v-checkbox
                id="affiliates-business-purposes-checkbox"
                v-model="affiliates_business_purposes"
              >
                <template v-slot:label>
                  <div id="affiliates-business-purposes-checkbox-text" class="checkbox-text">
                    Do not share information about my creditworthiness with NetCredit’s affiliates for their everyday business purposes.
                  </div>
                </template>
              </v-checkbox>
              <v-checkbox
                id="affiliates-marketing-checkbox"
                v-model="affiliates_marketing"
              >
                <template v-slot:label>
                  <div id="affiliates-marketing-checkbox-text" class="checkbox-text">
                    Do not share my personal information with NetCredit’s affiliates to market their products and services to me.
                  </div>
                </template>
              </v-checkbox>

              <v-checkbox 
                id="nonaffiliates-marketing-checkbox"
                v-model="nonaffiliates_marketing"
              >
                <template v-slot:label>
                  <div id="nonaffiliates-marketing-checkbox-text" class="checkbox-text">
                    Do not share my personal information with NetCredit's, Republic Bank & Trust Company's,
                    Transportation Alliance Bank's, Inc. d/b/a TAB Bank's, or Capital Community Bank's nonaffiliates to market their products and services to me.
                  </div>
                </template>
              </v-checkbox>

              <v-checkbox
                id="nonaffiliates-business-purposes-checkbox"
                v-model="nonaffiliates_business_purposes"
              >
                <template v-slot:label>
                  <div id="nonaffiliates-business-purposes-checkbox-text" class="checkbox-text">
                    Do not share my information with NetCredit's, Republic Bank & Trust Company's,
                    Transportation Alliance Bank's, Inc. d/b/a TAB Bank's, or Capital Community Bank's nonaffiliates for their everyday business purposes.
                  </div>
                </template>
              </v-checkbox>
            </v-col>
          </v-row>
        </v-form>

        <v-row justify="center" style="margin-top: -15px">
          <v-col cols="7">
            Please refer to the
            <a href="https://www.netcredit.com/privacy-policy">
              <strong>NetCredit Privacy Policy</strong>
            </a>, 
            <a href="https://www.netcredit.com/rbt-privacy-policy">
              <strong>Republic Bank & Trust Privacy Policy</strong>
            </a>, 
            <a href="https://www.netcredit.com/tab-privacy-policy">
              <strong>Transportation Alliance Bank Privacy Policy</strong>
            </a>, and
            <a href="https://www.netcredit.com/ccb-privacy-notice">
              <strong>Capital Community Bank Privacy Notice</strong>
            </a>
            for the definition of "affiliates" and "nonaffiliates" and for a description of what NetCredit,
            Republic Bank & Trust, Transportation Alliance Bank, and Capital Community Bank does with your personal information. 
          </v-col>
        </v-row>
        <v-row justify="center">
          <v-btn
            id="opt-out-button"
            ref="opt-out-button"
            :disabled="!formComplete"
            type="submit"
            x-large
            class="opt-out-button card-btn white--text"
            @click="submit"
          >
            Opt Out
          </v-btn>
        </v-row>
        <v-row justify="center">
          <v-col cols="7">
          This site is protected by reCAPTCHA and the Google <a href="https://policies.google.com/privacy">Privacy Policy</a> and
          <a href="https://policies.google.com/terms">Terms of Service</a> apply.
          </v-col>
        </v-row>
      </div>
    </v-app>
  </div>
</template>

<script>
import axios from 'axios';
import JumbotronView from '../JumbotronView.vue';
import {required} from 'vuelidate/lib/validators';

function isValidSSN(s) {
  return /^\d{9}$/.test(s);
}

function isValidDate(s) {
  //this is not a good way to validate dates because the behavior for
  //the date constructor with non-ISO 8601 strings is undefined
  //and implementation dependant, but this needs to match how it's checked
  //in registration in order for us to match records 

  if (!( /^\d{2}\/\d{2}\/\d{4}$/.test(s) )) return false;

  const givenBirthDate = new Date(s);

  return !isNaN(givenBirthDate)
}

export default {
  name: 'OptOut',
  components: {
    JumbotronView,
  },
  props: {
    recaptchaSiteKey: { type: String, required: false}, 
    loggedIn:   { type: Boolean, required: true },
    initialFirstName:  { type: String, required: false, default: '' },
    initialLastName:   { type: String, required: false, default: '' },
    initialSSN:        { type: String, required: false, default: '' },
    initialDOB:        { type: String, required: false, default: '' },
  },
  data() {
    return {
      firstName: this.initialFirstName,
      lastName: this.initialLastName,
      ssn: this.initialSSN,
      dob: this.initialDOB,
      affiliates_business_purposes: false,
      affiliates_marketing: false,
      nonaffiliates_business_purposes: false,
      nonaffiliates_marketing: false,
      opt_out_success: '',
      opt_out_message: '',
      recaptchaVerified: false,
    };
  },
  validations() {
    return {
      firstName: { required },
      lastName: { required },
      ssn: { required, isValidSSN },
      dob: { required, isValidDate }
    };
  },
  computed: {
    firstNameErrors() {
      const errors = [];
      if (!this.$v.firstName.$dirty) return errors;
      if (this.$v.firstName.$invalid) errors.push('Enter your first name.');
      return errors;
    },
    lastNameErrors() {
      const errors = [];
      if (!this.$v.lastName.$dirty) return errors;
      if (this.$v.lastName.$invalid) errors.push('Enter your last name.');
      return errors;
    },
    dobErrors() {
      this.dateFormatting();
      const errors = [];
      if (!this.$v.dob.$dirty) return errors;
      if (this.$v.dob.$invalid) errors.push('Please input a valid date of birth.');
      return errors;
    },
    ssnErrors() {
      const errors = [];
      if (!this.$v.ssn.$dirty || this.loggedIn) return errors;
      if (this.$v.ssn.$invalid) errors.push('Enter your 9-digit social security number.');
      return errors;
    },
    formComplete() {
      if (this.loggedIn) return true;
      if (!isValidSSN(this.ssn) || !isValidDate(this.dob)) return false;
      return (this.firstName && this.lastName && this.ssn && this.dob);
    },
    displayMessage() {
      return this.opt_out_success != null;
    }
  },
  mounted() {
    let style = document.createElement('style');
    style.type = 'text/css';
    style.innerHTML = '.grecaptcha-badge { visibility: hidden !important; }';
    document.head.appendChild(style);
  },
  methods: {
    clearInputs() {
      this.ssn = '';
      this.dob = '';
      this.firstName = '';
      this.lastName = '';
      this.$v.$reset();
    },
    clearCheckboxes() {
      this.affiliates_business_purposes = false;
      this.affiliates_marketing = false;
      this.nonaffiliates_business_purposes = false;
      this.nonaffiliates_marketing = false;
    },
    dateFormatting() {
      var date = this.dob;
      if (date.match(/^\d{2}$/) !== null) {
          this.dob = date + '/';
      } else if (date.match(/^\d{2}\/\d{2}$/) !== null) {
          this.dob = date + '/';
      }
    },
    async submit() {
      this.$v.$touch();

      let token = null;
      try {
        token = await this.$recaptcha('submit')
      } catch (e) {
        token = null;
      }

      const header = document.querySelector('meta[name="csrf-token"]').content;
      axios.post(
        '/opt-out',
        {
          first_name: this.firstName,
          last_name: this.lastName,
          ssn: this.ssn,
          dob: this.dob,
          opt_outs: {
            affiliates: {
              business_purposes: this.affiliates_business_purposes,
              marketing: this.affiliates_marketing},
            nonaffiliates: {
              business_purposes: this.nonaffiliates_business_purposes,
              marketing: this.nonaffiliates_marketing}
          },
          recaptcha_verified: token !== null,
          recaptcha_token: token
        },
        { headers: { 'X-CSRF-Token': header } }
      ).then((response) => {
        if (response.data !== undefined) {
          this.opt_out_message = response.data.message;
          this.opt_out_success = response.data.success ? 'success' : 'error';
        }
      }).catch((e) => {
        console.log(e);
        this.opt_out_message = "Your request could not be processed, please try again later. If the problem persists, you can submit your opt out request by phone with the NetCredit Customer Support team at (877) 392-2014, or by mail using the form in our <a href='https://www.netcredit.com/privacy-policy'>Privacy Policy</a>."
        this.opt_out_success = 'error';
      }).finally(() => {
        if (!this.loggedIn) this.clearInputs();
        this.clearCheckboxes();
      });
    },
  }
}
</script>

<style scoped lang="scss">
  .card {
    background: #ffffff;
    position: relative;
    margin-left: auto;
    margin-right: auto;

    @media (max-width: 768px) {
      box-shadow: 0px;
      padding: 15px;

    }

    @media (min-width: 768px) {
      box-shadow: 0px 3px 6px #00000029;
      padding: 40px;
      max-width: 1170px;
    }
  }

  .v-subheader {
    font-size: 1rem;
    padding: 0;
  }

  .left-column {
    padding-right: 3%;
  }

  .right-column {
    padding-left: 3%;
  }

  .v-checkbox {
    color: #007EB1;
  }

  .checkbox-text {
    color: #333333;
  }

  .v-application--is-ltr .v-input--selection-controls__input {
    margin-bottom: auto;
  }

  .v-icon.v-icon {
    color: #007EB1 !important;
  }

  .v-alert__content {
    text-align: center;
    color: white;

    .success {
      background-color: #4caf50;
    }

    .error {
      background-color: #DB2435 !important;
    }
  }

  .opt-out-button {
    background-color: #007EB1 !important;
  }

</style>
